import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'wouter';

import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { UnifiedSidebar } from '@/components/sidebar';
import { AccountSidebar } from '@/components/sidebar/AccountSidebar';
import { GlobalHeader } from '@/components/GlobalHeader';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MobileTabBar } from '@/components/MobileTabBar';
import { UserMenu } from '@/components/sidebar/UserMenu';
import { AccountRecordProvider } from '@/pages/account/AccountRecordContext';
import { AccountConfirmations } from '@/pages/account/AccountOverlays';

interface AccountLayoutProps {
  children: ReactNode;
}

/**
 * AR-380 — the shell for every page of the Account center.
 *
 * Structure: GlobalHeader → [ UnifiedSidebar (Level 1) | AccountSidebar (Level 2) |
 * content ], the same pattern the Finances module uses. Level 1 stays collapsed
 * on these pages, which is UnifiedSidebar's own default off an admin route.
 *
 * The record provider wraps the whole shell, not the page, because the Level 2
 * column reads the same record (the identity block and the settings search).
 */
export default function AccountLayout({ children }: AccountLayoutProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { favicon } = useTheme();
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (!favicon) return;
    const el = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (el) {
      el.href = favicon;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = favicon;
      document.head.appendChild(link);
    }
  }, [favicon]);

  const shouldRedirect = !isAuthenticated && !isLoading;

  useEffect(() => {
    if (shouldRedirect) navigate('/login');
  }, [shouldRedirect, navigate]);

  if (!isAuthenticated) {
    if (isLoading) return <div>Loading...</div>;
    return null;
  }

  return (
    <AccountRecordProvider>
      <div
        data-account-shell
        data-location={location}
        className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-[#101415]"
      >
        <GlobalHeader />
        <ImpersonationBanner />
        <div className="flex min-h-0 flex-1" data-account-body>
          <div className="hidden md:flex">
            <UnifiedSidebar />
          </div>
          <div className="hidden md:flex">
            <AccountSidebar />
          </div>
          <main className="flex-1 overflow-auto bg-white dark:bg-[#101415]" data-account-main>
            {children}
          </main>
        </div>
        {/* The phone shell: the two rails leave the flow, the primary
            destinations become a bottom tab bar, and the Account column moves
            into the More sheet above it. */}
        <MobileTabBar footer={<UserMenu expanded inline />}>
          {(close) => <AccountSidebar inDrawer onNavigated={close} />}
        </MobileTabBar>
        <AccountConfirmations />
      </div>
    </AccountRecordProvider>
  );
}
